import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

interface Actualite {
  id: string;
  titre: string;
  description: string;
  contenu: string;
  type: 'evenement' | 'don_sang' | 'campagne' | 'information' | 'autre';
  image_url: string | null;
  date_debut: string | null;
  date_fin: string | null;
  lieu: string | null;
  epingle: boolean;
  date_creation: string;
  vues: number;
}

export default function ActualitesPage() {
  const [actualites, setActualites] = useState<Actualite[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('tous');

  useEffect(() => {
    fetchActualites();
  }, []);

  const fetchActualites = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('actualites')
        .select('*')
        .eq('actif', true)
        .order('epingle', { ascending: false })
        .order('date_creation', { ascending: false });

      if (error) throw error;
      setActualites(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des actualités:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      evenement: 'Événement',
      don_sang: 'Don de sang',
      campagne: 'Campagne',
      information: 'Information',
      autre: 'Autre'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      evenement: 'ri-calendar-event-line',
      don_sang: 'ri-heart-pulse-line',
      campagne: 'ri-megaphone-line',
      information: 'ri-information-line',
      autre: 'ri-file-list-line'
    };
    return icons[type] || 'ri-file-list-line';
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      evenement: 'bg-purple-500',
      don_sang: 'bg-red-500',
      campagne: 'bg-blue-500',
      information: 'bg-green-500',
      autre: 'bg-gray-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const filteredActualites = selectedType === 'tous' 
    ? actualites 
    : actualites.filter(a => a.type === selectedType);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-clinic-green to-clinic-blue text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
              <span className="font-semibold">Actualités &amp; Événements</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Restez informés de nos activités
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Découvrez nos événements, campagnes de santé publique et toutes les actualités de la Clinique Sainte Famille
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedType('tous')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                selectedType === 'tous'
                  ? 'bg-clinic-green text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setSelectedType('don_sang')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                selectedType === 'don_sang'
                  ? 'bg-red-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Dons de sang
            </button>
            <button
              onClick={() => setSelectedType('evenement')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                selectedType === 'evenement'
                  ? 'bg-purple-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Événements
            </button>
            <button
              onClick={() => setSelectedType('campagne')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                selectedType === 'campagne'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Campagnes
            </button>
            <button
              onClick={() => setSelectedType('information')}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                selectedType === 'information'
                  ? 'bg-green-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Informations
            </button>
          </div>
        </div>
      </section>

      {/* Actualités Grid */}
      <section className="py-10 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <i className="ri-loader-4-line text-5xl text-clinic-green animate-spin mb-4"></i>
                <p className="text-gray-600">Chargement des actualités...</p>
              </div>
            </div>
          ) : filteredActualites.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-600">Aucune actualité disponible pour le moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredActualites.map((actualite) => (
                <div
                  key={actualite.id}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border-2 border-gray-100 hover:border-clinic-green/30"
                >
                  {/* Image */}
                  <div className="relative h-56 bg-gradient-to-br from-clinic-blue/10 to-clinic-green/10 overflow-hidden">
                    {actualite.image_url ? (
                      <img
                        src={actualite.image_url}
                        alt={actualite.titre}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className={`${getTypeIcon(actualite.type)} text-6xl text-clinic-blue/30`}></i>
                      </div>
                    )}
                    
                    {/* Badge Type */}
                    <div className={`absolute top-4 left-4 ${getTypeColor(actualite.type)} text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2 whitespace-nowrap`}>
                      {getTypeLabel(actualite.type)}
                    </div>

                    {/* Badge Épinglé */}
                    {actualite.epingle && (
                      <div className="absolute top-4 right-4 bg-yellow-500 text-white p-2 rounded-full shadow-lg">
                        <i className="ri-pushpin-fill text-lg"></i>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-clinic-blue-dark mb-3 line-clamp-2 group-hover:text-clinic-green transition-colors">
                      {actualite.titre}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {actualite.description}
                    </p>

                    {/* Dates et Lieu */}
                    <div className="space-y-2 mb-4">
                      {actualite.date_debut && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <i className="ri-calendar-line text-clinic-green"></i>
                          <span>
                            {formatDate(actualite.date_debut)}
                            {actualite.date_fin && actualite.date_fin !== actualite.date_debut && 
                              ` - ${formatDate(actualite.date_fin)}`
                            }
                          </span>
                        </div>
                      )}
                      
                      {actualite.lieu && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <i className="ri-map-pin-line text-clinic-green"></i>
                          <span className="line-clamp-1">{actualite.lieu}</span>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="ri-eye-line"></i>
                        <span>{actualite.vues} vues</span>
                      </div>
                      
                      <Link
                        to={`/actualites/${actualite.id}`}
                        className="text-clinic-green font-semibold hover:text-clinic-green/80 transition-colors flex items-center gap-2 whitespace-nowrap"
                      >
                        En savoir plus
                        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-clinic-blue to-clinic-blue-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <i className="ri-heart-pulse-line text-6xl mb-6 inline-block"></i>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Participez à nos événements
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
            Rejoignez-nous lors de nos campagnes de santé publique et événements communautaires. Ensemble, construisons une communauté en meilleure santé.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-clinic-blue rounded-full hover:bg-gray-100 transition-all font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 whitespace-nowrap"
          >
            <i className="ri-mail-line text-2xl"></i>
            Contactez-nous
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
